import { IssueRegistry } from "../models/IssueRegistry";
import { handleResponse } from './errorHandler';
import AccessTokenManager from './accessTokenManager';
import { Property, PropertyRegistry, EnrichmentStatus } from "../models/property";
import { WorkItem } from "../models/workItem";

const workAnalyzerBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';

async function getProperty(id: string) : Promise<Property>{
  const url = `${workAnalyzerBaseUrl}/properties/${id}`;
  const token = AccessTokenManager.getAccessToken();
  
  const response = await fetch(url, { 
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  const registry = await handleResponse(response);
  return registry.propertyData;
}

async function getPropertyEnrichmentStatus(id: string): Promise<{status: EnrichmentStatus}> {
  const response = await fetch(`${workAnalyzerBaseUrl}/properties/${id}/enrichment-status`, { 
    headers: {
      'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`
    }
  });
  return await handleResponse(response);
}

async function getPropertyRegistry() : Promise<PropertyRegistry>{
    try {
      console.log('Fetching property registry...');
      const token = AccessTokenManager.getAccessToken();
      console.log('Using token:', token ? 'Token available' : 'No token');
      
      const response = await fetch(`${workAnalyzerBaseUrl}/properties/`, { 
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Property registry response status:', response.status);
      const registry = await handleResponse(response);
      console.log('Property registry data:', registry);
      
      if (!registry.propertyRegistry) {
        console.warn('Registry does not contain propertyRegistry array:', registry);
        // If the registry doesn't have the expected format, create a default structure
        return { propertyRegistry: [] };
      }
      
      return registry;
    } catch (error) {
      console.error('Error in getPropertyRegistry:', error);
      // Return an empty registry on error
      return { propertyRegistry: [] };
    }
}


async function updateProperty(propertyId : string, updatedProperty : Property){
    try{
        const requestBody = { address: updatedProperty.address, property: updatedProperty };
        console.log(requestBody);
        const response = await fetch(`${workAnalyzerBaseUrl}/properties/${propertyId}`, { 
          method: 'PUT',
          body: JSON.stringify(requestBody),
          headers: {
            'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`,
            'Content-Type': 'application/json',
          }
        });
        const propertyResponse = await handleResponse(response);
        return propertyResponse.id;
      }catch(e){
        console.log(e);
        return null;
      }
}


async function getPropertyImageUrl(propertyId: string, imageId: string) : Promise<string>{
    try{
        const response = await fetch(`${workAnalyzerBaseUrl}/properties/${propertyId}/image/${imageId}`, { 
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`,
          }
        });
        const responseBody = await handleResponse(response);
        return responseBody.url;
      }catch(e){
        console.log(e);
        return "";
      }
}

async function createInspectionReportFromImageRegistry(propertyId: string){
    try{
      const response = await fetch(`${workAnalyzerBaseUrl}/properties/${propertyId}/inspection/picture/report`, { 
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`,
        }
      });
      const propertyResponse = await handleResponse(response);
      return propertyResponse.id;
    }catch(e){
      console.log(e);
      return null;
    }
}


async function createProperty(streetAddress: string, city: string, state: string, zip: string) : Promise<any>{
    try{
      const requestBody = { streetAddress: streetAddress, city: city, state: state, zip: zip};
      const response = await fetch(`${workAnalyzerBaseUrl}/properties/`, { 
        method: 'POST',
        body: JSON.stringify(requestBody),
        headers: {
          'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`,
          'Content-Type': 'application/json',
        }
      });
      const propertyResponse = await handleResponse(response);
      return propertyResponse.id;
    }catch(e){
      console.log(e);
      return null;
    }
}

async function createPropertyInspectionRecord(propertyId: string, file: File) : Promise<any>{
    const formData = new FormData();
    formData.append('file', file);
  
    try {
      const response = await fetch(`${workAnalyzerBaseUrl}/properties/${propertyId}/inspection/`, {
        method: 'POST',
        body: formData,
        headers: {
            'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`
          }
      });
  
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
  
      const result = await handleResponse(response);
      return result;
    }catch(e){
        console.log(e);
        return undefined;
    }
}


async function createWorkItemFromInspectionRecord(propertyId: string, inspectionId: string) : Promise<any>{
  try {

    const response = await fetch(`${workAnalyzerBaseUrl}/properties/${propertyId}/inspection/${inspectionId}/workitem`, {
      method: 'POST',
      body:"{}",
      headers: {
          'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`,
          'Content-Type': 'application/json',
        }
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const result = await handleResponse(response);
    return result;
  }catch(e){
      console.log(e);
      return undefined;
  }
}


async function createQuoteAnalysisRecord(propertyId: string, workItemId: string, file: File) : Promise<any>{
    const formData = new FormData();
    formData.append('file', file);
  
    try {
      const response = await fetch(`${workAnalyzerBaseUrl}/properties/${propertyId}/workitem/${workItemId}/quote/`, {
        method: 'POST',
        body: formData,
        headers: {
            'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`
          }
      });
  
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
  
      const result = await handleResponse(response);
      return result;
    }catch(e){
        console.log(e);
        return undefined;
    }
}

async function analyzeWorkItem(propertyId: string, workItemId: string)
{
    try {
        
        const response = await fetch(`${workAnalyzerBaseUrl}/properties/${propertyId}/workitem/${workItemId}/`, {
          method: 'POST',
          headers: {
              'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`,
            }
        });
    
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
    
        const result = await handleResponse(response);
        return result;
      }catch(e){
          console.log(e);
          return undefined;
      }
}

async function createWorkItem(propertyId: string, workItem : WorkItem){
    try {
        
        const response = await fetch(`${workAnalyzerBaseUrl}/properties/${propertyId}/workitem/`, {
          method: 'POST',
          body: JSON.stringify(workItem),
          headers: {
              'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`,
              'Content-Type': 'application/json',
            }
        });
    
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
    
        const result = await handleResponse(response);
        return result;
      }catch(e){
          console.log(e);
          return undefined;
      }
}

async function addPictureToPropertyInspections(propertyId: string, file: File) : Promise<any>{
  const formData = new FormData();
  formData.append('file', file);
  try {
    const response = await fetch(`${workAnalyzerBaseUrl}/properties/${propertyId}/inspection/picture`, {
      method: 'POST',
      body: formData,
      headers: {
          'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`
        }
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const result = await handleResponse(response);
    return result;
  }catch(e){
      console.log(e);
      return undefined;
  }
}

async function getPropertyInspectionRegistry(propertyId: string) : Promise<any>{
  const formData = new FormData();
  try {
    const response = await fetch(`${workAnalyzerBaseUrl}/properties/${propertyId}/inspection/picture/registry`, {
      method: 'GET',
      headers: {
          'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`
        }
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const result = await handleResponse(response);
    return result;
  }catch(e){
      console.log(e);
      return undefined;
  }
}


async function createPropertyImprovementRecord(propertyId: string) : Promise<any>{  
    try {
      const response = await fetch(`${workAnalyzerBaseUrl}/properties/${propertyId}/improvement/`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`,
          }
      });
  
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
  
      const result = await handleResponse(response);
      return result;
    }catch(e){
        console.log(e);
        return undefined;
    }
}

export default {
    getProperty,
    getPropertyRegistry,
    createProperty,
    updateProperty,
    getPropertyImageUrl,
    createPropertyInspectionRecord,
    createPropertyImprovementRecord,
    createWorkItem,
    analyzeWorkItem,
    createQuoteAnalysisRecord,
    addPictureToPropertyInspections,
    getPropertyInspectionRegistry,
    createInspectionReportFromImageRegistry,
    createWorkItemFromInspectionRecord,
    getPropertyEnrichmentStatus
} as const;